import { useState, useEffect, useCallback } from 'react';
import { db } from '@/config/firebase';
import { doc, getDoc } from 'firebase/firestore';

// Field names exactly as they appear in Firestore
export const PLACEMENT_TRANSITION_FIELDS = [
  'Skip Legacy Campaigns and Go Straight to Placement Campaigns',
  'Enable Legacy Campaign Bid Increase Transition System',
  'Prevent Legacy Campaigns from Being Auto Paused',
  'Pause Terms in Exact Campaign for Placement Transition',
  'Pause Terms in PTA Campaign for Placement Transition'
] as const;

export type PlacementTransitionField = typeof PLACEMENT_TRANSITION_FIELDS[number];

export interface PlacementTransitionData {
  'Skip Legacy Campaigns and Go Straight to Placement Campaigns': string;
  'Enable Legacy Campaign Bid Increase Transition System': string;
  'Prevent Legacy Campaigns from Being Auto Paused': string;
  'Pause Terms in Exact Campaign for Placement Transition': string;
  'Pause Terms in PTA Campaign for Placement Transition': string;
}

export interface PlacementTransitionEntry {
  field: PlacementTransitionField;
  value: string;
}

interface UsePlacementTransitionInputsResult {
  data: PlacementTransitionEntry[] | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Hook to fetch placement transition inputs from pmt2_data collection
 * Fetches data from /pmt2_data/{companyName}/products/{asin}
 */
export const usePlacementTransitionInputs = (
  companyName?: string,
  asin?: string
): UsePlacementTransitionInputsResult => {
  const [data, setData] = useState<PlacementTransitionEntry[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchPlacementTransitionInputs = useCallback(async () => {
    if (!companyName || !asin) {
      setData(null);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Fetch document from pmt2_data collection
      const docRef = doc(db, 'pmt2_data', companyName, 'products', asin);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const docData = docSnap.data();
        
        // Process each field and create entries array
        const entries: PlacementTransitionEntry[] = PLACEMENT_TRANSITION_FIELDS.map(field => {
          const value = docData[field];
          
          // Handle string values, convert null/undefined to "-"
          let processedValue: string;
          if (value === null || value === undefined || value === '') {
            processedValue = '-';
          } else if (typeof value === 'string') {
            processedValue = value;
          } else {
            // Convert other types to string
            processedValue = String(value);
          }

          return {
            field,
            value: processedValue
          };
        });

        setData(entries);
      } else {
        // Document doesn't exist, set all fields to "-"
        const entries: PlacementTransitionEntry[] = PLACEMENT_TRANSITION_FIELDS.map(field => ({
          field,
          value: '-'
        }));
        setData(entries);
      }
    } catch (err) {
      console.error('Error fetching placement transition inputs:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch placement transition inputs'));
    } finally {
      setIsLoading(false);
    }
  }, [companyName, asin]);

  const refetch = useCallback(() => {
    fetchPlacementTransitionInputs();
  }, [fetchPlacementTransitionInputs]);

  useEffect(() => {
    fetchPlacementTransitionInputs();
  }, [fetchPlacementTransitionInputs]);

  return {
    data,
    isLoading,
    error,
    refetch
  };
};

export default usePlacementTransitionInputs;